import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, ArrowRight, Building2, Check, ChevronRight,
  Compass, Cpu, ExternalLink, GraduationCap, Handshake,
  Network, Sparkles, Users, X, Zap,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PROFILES } from '../config/activityProfileConfig2';
import { Header } from './HomePageLab';


/* ── DESIGN TOKENS ─────────────────────────────────────────── */
const T = {
  bg:       '#020617',        // aligné avec Academia (bleu nuit profond)
  bgPanel:  'rgba(2,6,23,0.85)',  // fond des panneaux latéraux
  bgCard:   'rgba(255, 255, 255, 0.03)',  // plus transparent, plus "lab"
  border:   'rgba(34,211,238,0.08)',   // indigo très léger
  accent:   '#f59e0b',        // ambre/orange (cibles)
  accentG:  '#f59e0b',        // même couleur pour homogénéité
  indigo:     '#22d3ee',        // indigo (services)
  indigoG:    '#22d3ee',        // même couleur
  green:    '#10b981',        // vert émeraude (statuts)
  purple:   '#8b5cf6',
  text:     '#f7f4ef',
  textDim:  'rgba(247, 244, 239, 0.88)',
  textMute: 'rgba(255, 255, 255, 0.86)',
  scan:     'rgba(34,211,238,0.025)',
};

/* ── COULEURS PROFILS ───────────────────────────────────────── */
const PROFILE_COLORS = {
  orange:  '#f97316',
  sky:     '#0ea5e9',
  fuchsia: '#d946ef',
  emerald: '#10b981',
  purple:  '#8b5cf6',
};

function profileColor(p) {
  return PROFILE_COLORS[p?.accentColor] || T.accent;
}

/* ── MÉTRIQUES ENTITÉS ──────────────────────────────────────── */
const METRICS = {
  particuliers:     [{ k:'Trajectoire active', v:94, u:'%', max:100 },{ k:'Bifurcations', v:1287, u:'' },{ k:'Cohérence déc.', v:88, u:'%', max:100 },{ k:'Complétion', v:97, u:'%', max:100 }],
  entreprises:      [{ k:'Transform. comport.', v:86, u:'%', max:100 },{ k:'Réflexes ancrés', v:93, u:'%', max:100 },{ k:'Pression simulée', v:247, u:'h' },{ k:'Équipes actives', v:34, u:'' }],
  collectivites:    [{ k:'Territoires couverts', v:18, u:'' },{ k:'Situations reprod.', v:64, u:'' },{ k:'Insertion simulée', v:91, u:'%', max:100 },{ k:'Complexité moy.', v:42, u:'nœuds' }],
  institutions:     [{ k:'Disciplines modél.', v:12, u:'' },{ k:'Invariance validée', v:99, u:'%', max:100 },{ k:'Apprenants actifs', v:5600, u:'' },{ k:'Sessions simult.', v:240, u:'' }],
  conception:       [{ k:'Architectures caus.', v:16, u:'' },{ k:'Nœuds décis.', v:3402, u:'' },{ k:'Cohérence moteur', v:98, u:'%', max:100 },{ k:'Modules déployés', v:29, u:'' }],
  developpement:    [{ k:'Instances SaaS', v:78, u:'' },{ k:'Latence déc.', v:12, u:'ms' },{ k:'Invariant score', v:100, u:'%', max:100 },{ k:'API calls / jour', v:'2.4M', u:'' }],
  edition:          [{ k:'Situations éditées', v:410, u:'' },{ k:'Frictions calibrées', v:97, u:'%', max:100 },{ k:'Temps / décision', v:8.3, u:'s' },{ k:'Traces doc.', v:112000, u:'' }],
  commercialisation:[{ k:'Profils actifs', v:9200, u:'' },{ k:'Territoires touchés', v:42, u:'' },{ k:'Taux conversion', v:23, u:'%', max:100 },{ k:'Diffusion org.', v:67, u:'%', max:100 }],
};

/* ── ENTITÉS ────────────────────────────────────────────────── */
/*
  profileIds : profils disponibles pour cette cible
  Logique :
   - particuliers     → panneau dédié (plans individuels)
   - entreprises      → corporate, creative, health, consulting
   - collectivites    → consulting, education
   - institutions     → education, corporate
*/
const CIBLES = [
  {
    icon: Users, label:'Particuliers', desc:'Apprenants & autodidactes',
    id:'particuliers', color:T.accent, signal:'FLUX ENTRANT',
    profileIds: [],
    connectTo: ['commercialisation','developpement'],
  },
  {
    icon: Building2, label:'Entreprises', desc:'RH, managers, équipes',
    id:'entreprises', color:T.accent, signal:'FLUX ENTRANT',
    profileIds: ['corporate','creative','health','consulting'],
    connectTo: ['conception','commercialisation'],
  },
  {
    icon: Handshake, label:'Collectivités', desc:'Missions locales, territoires',
    id:'collectivites', color:T.indigo, signal:'FLUX ENTRANT',
    profileIds: ['consulting','education'],
    connectTo: ['edition','conception'],
  },
  {
    icon: GraduationCap, label:'Institutions', desc:'Éducation nationale, universités',
    id:'institutions', color:T.indigo, signal:'FLUX ENTRANT',
    profileIds: ['education','corporate'],
    connectTo: ['edition','developpement'],
  },
];

const SERVICES = [
  { icon: Sparkles, label:'Conception',       detail:'Plateformes numériques sur mesure',   id:'conception',       color:T.accentG, connectTo:['entreprises','collectivites'], signal:'FLUX SORTANT', compatProfiles:['education','corporate'] },
  { icon: Cpu,      label:'Développement',    detail:'Solutions logicielles SaaS',           id:'developpement',    color:T.accentG, connectTo:['particuliers','institutions'], signal:'FLUX SORTANT', compatProfiles:['corporate','consulting'] },
  { icon: Compass,  label:'Édition',          detail:'E-learning, formation, transmission',  id:'edition',          color:T.indigoG,   connectTo:['collectivites','institutions'], signal:'FLUX SORTANT', compatProfiles:['education','consulting'] },
  { icon: Network,  label:'Commercialisation',detail:"Diffusion dédiée à l'apprentissage",   id:'commercialisation',color:T.indigoG,   connectTo:['particuliers','entreprises'],  signal:'FLUX SORTANT', compatProfiles:['education','corporate','creative','health','consulting'] },
];

const ALL_ENTITIES = [...CIBLES, ...SERVICES];

/* ── PLANS PARTICULIER ──────────────────────────────────────── */
const PLANS_PART = [
  { id:'decouverte', name:'Découverte', price:'0 €', period:'', color:T.indigo,   features:["Centre d'Ajustement — cas de base",'Parcours de découverte','Débrief automatique'] },
  { id:'parcours',   name:'Parcours',   price:'10 €', period:'/mois', trial:'30 j', color:T.accentG, features:['Tous les cas & ateliers','Progression documentée','Certifications individuelles'] },
  { id:'praticien',  name:'Praticien',  price:'25 €', period:'/mois', trial:'30 j', color:T.accent, features:['Tout Parcours inclus','Corpus étendu','Export traces décisionnelles'] },
];

/* ── APERÇU ARBRE PERSONAS ──────────────────────────────────── */
const PERSONA_TREE = {
  education: [
    { label: 'Centre de formation',    sub: ['CFA', 'Org. privé', 'Org. public'] },
    { label: 'Établissement scolaire', sub: ['Lycée pro', 'Université', 'École ingénieur'] },
    { label: 'Formateur indépendant',  sub: ['Auto-entrepreneur', 'Société unipersonnelle', 'Portage salarial'] },
    { label: 'Plateforme e-learning',  sub: ['MOOC / COOC', 'LMS propriétaire', 'Marketplace formation'] },
  ],
 
  corporate: [
    { label: 'PME < 250 salariés', sub: ['Industrie', 'Services', 'Commerce'] },
    { label: 'ETI 250–5000',        sub: ['Multi-sites France', 'Groupe international', 'Holding'] },
    { label: 'Grand groupe > 5000', sub: ['Siège social', 'BU / Division', 'Filiale'] },
    { label: 'Startup / Scale-up',  sub: ['Seed / Amorçage', 'Série A–B', 'Late stage'] },
  ],
 
  creative: [
    { label: 'Agence créative',      sub: ['Communication', 'Publicité', 'Studio design'] },
    { label: 'Studio audiovisuel',   sub: ['Cinéma / TV', 'Podcast & radio', 'Jeu vidéo'] },
    { label: 'Freelance créatif',    sub: ['Photographe', 'Motion designer', 'Illustrateur'] },
    { label: 'Production & Presse',  sub: ["Maison d'édition", 'Média en ligne', 'Intermittent'] },
  ],
 
  health: [
    { label: 'Clinique / Cabinet',      sub: ['Cabinet libéral', 'Clinique privée', 'MSP'] },
    { label: 'CHU / Hôpital',           sub: ['CHU / CHR', 'Hôpital public', 'EHPAD'] },
    { label: 'Professionnel libéral',   sub: ['Médecin', 'IDE', 'Kiné', 'Pharmacien', 'Ophtalmologue', '…'] },
    { label: 'Organisme de DPC',        sub: ['ODPC agréé ANDPC', 'Simulation en santé'] },
  ],
 
  consulting: [
    { label: 'Cabinet de conseil',    sub: ['Stratégie', 'RH / Management', 'Juridique / Expertise'] },
    { label: 'ESN',                   sub: ['Développement', 'Infrastructure', 'Data & IA'] },
    { label: 'Freelance B2B',         sub: ['Consultant indépendant', 'Coach certifié', 'Formateur-conseil'] },
    { label: 'Accompagnement',        sub: ['Bilan de compétences', 'VAE', 'Outplacement'] },
  ],
};

/* ── HOOK MOBILE ────────────────────────────────────────────── */
function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < 900);
    fn(); window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return m;
}

/* ── GRILLE CAUSALE ─────────────────────────────────────────── */
function CausalGrid({ activeColor }) {
  const cols = 9, rows = 7;
  const nodes = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++)
      nodes.push({ id:`${r}-${c}`, x:(c/(cols-1))*100, y:(r/(rows-1))*100, delay:(r+c)*0.07 });

  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
      {Array.from({length:rows}).map((_,r) => (
        <motion.div key={`h${r}`}
          animate={{ opacity: activeColor ? [0.04,0.1,0.04] : [0.02,0.04,0.02] }}
          transition={{ duration:4+r, repeat:Infinity, delay:r*0.3 }}
          style={{ position:'absolute', left:0, right:0, top:`${(r/(rows-1))*100}%`, height:1, background:`linear-gradient(90deg,transparent,${activeColor||T.indigo}40,transparent)` }}
        />
      ))}
      {Array.from({length:cols}).map((_,c) => (
        <motion.div key={`v${c}`}
          animate={{ opacity: activeColor ? [0.04,0.1,0.04] : [0.02,0.04,0.02] }}
          transition={{ duration:5+c*0.4, repeat:Infinity, delay:c*0.25 }}
          style={{ position:'absolute', top:0, bottom:0, left:`${(c/(cols-1))*100}%`, width:1, background:`linear-gradient(180deg,transparent,${activeColor||T.accent}30,transparent)` }}
        />
      ))}
      {nodes.map(n => (
        <motion.div key={n.id}
          animate={{ opacity: activeColor ? [0.15,0.65,0.15] : [0.03,0.16,0.03], scale: activeColor ? [0.8,1.5,0.8] : [0.8,1,0.8] }}
          transition={{ duration:3+Math.random()*2, repeat:Infinity, delay:n.delay }}
          style={{ position:'absolute', left:`${n.x}%`, top:`${n.y}%`, width:3, height:3, borderRadius:'50%', background:activeColor||T.indigo, transform:'translate(-50%,-50%)' }}
        />
      ))}
    </div>
  );
}

/* ── CARTE ENTITÉ ───────────────────────────────────────────── */
function ControlPanel({ icon:Icon, label, desc, color, id, isActive, isDimmed, onActivate, signal }) {
  const metrics = METRICS[id] || [];
  const b1 = useRef(Math.floor(40+Math.random()*50)).current;
  const b2 = useRef(Math.floor(30+Math.random()*60)).current;
  const b3 = useRef(Math.floor(55+Math.random()*40)).current;

  return (
    <motion.div onClick={() => onActivate(id)}
      animate={{ opacity: isDimmed ? 0.25 : 1, scale: isActive ? 1.02 : 1 }}
      transition={{ duration:0.3, type:'spring', stiffness:200, damping:22 }}
      style={{ cursor:'pointer', position:'relative' }}
    >
      <motion.div
        animate={{ borderColor: isActive ? color : T.border, boxShadow: isActive ? `0 0 0 1px ${color}55,0 0 28px ${color}18,inset 0 0 24px ${color}04` : '0 0 0 1px transparent' }}
        transition={{ duration:0.22 }}
        style={{ background: isActive ? `linear-gradient(145deg,${T.bgCard},${T.bgPanel})` : T.bgCard, border:`1px solid ${isActive?color:T.border}`, borderRadius:'10px', padding:'0.8rem', fontFamily:"'DM Mono',monospace", overflow:'hidden', position:'relative' }}
      >
        <div style={{ position:'absolute', inset:0, backgroundImage:`repeating-linear-gradient(0deg,${T.scan} 0,${T.scan} 1px,transparent 1px,transparent 4px)`, pointerEvents:'none', borderRadius:'10px' }} />

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.6rem', position:'relative', zIndex:1 }}>
          <div style={{ width:28, height:28, borderRadius:'7px', background:`${color}14`, border:`1px solid ${color}28`, display:'flex', alignItems:'center', justifyContent:'center', color, flexShrink:0 }}>
            <Icon size={13} strokeWidth={1.7} />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'0.78rem', fontWeight:700, color: isActive ? color : T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', letterSpacing:'0.04em' }}>{label}</div>
            <div style={{ fontSize:'0.56rem', color:T.textMute, marginTop:'0.08rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{desc}</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'0.12rem', flexShrink:0 }}>
            <div style={{ width:5, height:5, borderRadius:'50%', background: isActive ? color : '#1e3040', boxShadow: isActive ? `0 0 10px ${color}` : 'none' }} />
            <div style={{ fontSize:'0.42rem', color: isActive ? color : T.textMute, letterSpacing:'0.14em' }}>{isActive ? '● ON' : '○ —'}</div>
          </div>
        </div>

        {/* Barres d'activité */}
        <div style={{ display:'flex', gap:'0.3rem', marginBottom:'0.5rem', position:'relative', zIndex:1 }}>
          {[b1,b2,b3].map((v,idx) => (
            <div key={idx} style={{ flex:1, height:2, background:'#0c1620', borderRadius:1, overflow:'hidden' }}>
              <motion.div animate={{ width:[`${v}%`,`${Math.min(v+9,100)}%`,`${v}%`] }} transition={{ duration:3+idx, repeat:Infinity, ease:'easeInOut', delay:idx*0.55 }}
                style={{ height:'100%', background:color, opacity: isActive ? 0.9 : 0.32, borderRadius:1 }} />
            </div>
          ))}
        </div>

        {/* 2 métriques */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.3rem', position:'relative', zIndex:1 }}>
          {metrics.slice(0,2).map((m,i) => (
            <div key={i} style={{ background:'#070d13', borderRadius:'6px', padding:'0.3rem 0.4rem', border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:'0.44rem', color:T.textMute, marginBottom:'0.1rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.k}</div>
              <div style={{ fontSize:'0.72rem', fontWeight:700, color: isActive ? color : T.text }}>{m.v}<span style={{ fontSize:'0.4rem', color:T.textMute, marginLeft:'0.12rem' }}>{m.u}</span></div>
            </div>
          ))}
        </div>

        {/* Pied */}
        <div style={{ marginTop:'0.45rem', display:'flex', justifyContent:'space-between', alignItems:'center', position:'relative', zIndex:1 }}>
          <span style={{ fontSize:'0.44rem', color:T.textMute, letterSpacing:'0.14em' }}>{signal}</span>
          <ChevronRight size={9} color={isActive ? color : T.textMute} />
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── LOGO HUB ───────────────────────────────────────────────── */
function LogoHub({ logoRef, phase, activeColor, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const handleClick = async () => {
    if (!onClick || isActivating) return;

    setIsActivating(true);

    // Durée plus lente et organique
    await new Promise(resolve => setTimeout(resolve, 1200));

    onClick();
    setIsActivating(false);
  };

  return (
    <motion.div
      ref={logoRef}
      style={{
        position: 'relative',
        margin: '0 auto',
        width: 'clamp(80px,12vw,120px)',
        height: 'clamp(80px,12vw,120px)',
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {/* Anneaux rotatifs (légèrement ralentis) */}
      {[1.35, 1.6, 1.9].map((sc, i) => (
        <motion.div
          key={i}
          animate={{ rotate: i % 2 === 0 ? [0, 360] : [360, 0] }}
          transition={{ duration: 28 + i * 10, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            inset: `${-(sc - 1) * 50}%`,
            borderRadius: '50%',
            border: `1px dashed ${activeColor ? activeColor + '38' : T.border}`,
          }}
        />
      ))}

      {/* Halo organique */}
      <motion.div
        animate={{
          opacity: isActivating ? [0.4, 0.85, 0.5] : activeColor ? [0.25, 0.55, 0.25] : hovered ? [0.1, 0.25, 0.1] : [0.05, 0.15, 0.05],
          scale: isActivating ? [1, 1.45, 1.1] : activeColor ? [1, 1.25, 1] : hovered ? [1, 1.18, 1] : [1, 1.22, 1],
        }}
        transition={{ 
          duration: isActivating ? 2.8 : 3.5, 
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          inset: '-45%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${activeColor || T.indigo}50 0%, transparent 68%)`,
          filter: 'blur(24px)',
        }}
      />

      {/* Boîte du logo */}
      <motion.div
        animate={{
          boxShadow: isActivating
            ? `0 0 0 2px ${activeColor || '#a0f0ff'}70, 0 0 55px ${activeColor || '#60d0ff'}40, inset 0 0 35px ${activeColor || '#40b0ff'}25`
            : hovered
            ? `0 0 0 1px ${T.accent}50, 0 0 35px ${T.accent}25`
            : `0 0 0 1px ${T.border}, 0 4px 24px rgba(0,0,0,0.5)`,
          scale: isActivating ? [1, 1.06, 1.03] : hovered ? 1.04 : 1,
        }}
        transition={{ duration: isActivating ? 2.2 : 0.4, ease: "easeInOut" }}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '22px',
          background: `linear-gradient(145deg, ${T.bgCard}, #030608)`,
          border: `1px solid ${isActivating ? (activeColor || '#88ddff') : T.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <img src="/logo512.png" alt="PESACORE" style={{ width: '62%', height: '62%', objectFit: 'contain' }} />

        {/* Ondes organiques d'activation */}
        <AnimatePresence>
          {isActivating && (
            <>
              {/* Onde principale lente et douce */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: [0.6, 2.8], opacity: [0, 0.35, 0] }}
                transition={{ duration: 1.8, ease: "easeOut" }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '22px',
                  background: `radial-gradient(circle, ${activeColor || T.indigo}60 20%, transparent 70%)`,
                  zIndex: 3,
                }}
              />

              {/* Deuxième onde décalée plus large */}
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: [0.4, 3.2], opacity: [0, 0.22, 0] }}
                transition={{ duration: 2.4, delay: 0.3, ease: "easeOut" }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '22px',
                  background: `radial-gradient(circle, ${activeColor || '#a0f0ff'}45 15%, transparent 75%)`,
                  zIndex: 3,
                }}
              />
            </>
          )}
        </AnimatePresence>

        {/* Onde subtile sur hover */}
        <AnimatePresence>
          {(hovered || activeColor) && !isActivating && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: [0, 0.18, 0], scale: [0.8, 2.1] }}
              transition={{ duration: 2.8, repeat: Infinity }}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '22px',
                background: `radial-gradient(circle, ${activeColor || T.accent}40 30%, transparent 70%)`,
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Label PESACORE */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: phase >= 3 ? 0.3 : 0 }}
        style={{
          position: 'absolute',
          top: 'calc(100% + 0.55rem)',
          left: '50%',
          transform: 'translateX(-50%)',
          whiteSpace: 'nowrap',
          fontFamily: "'DM Mono', monospace",
          fontSize: '0.42rem',
          letterSpacing: '0.38em',
          textTransform: 'uppercase',
          color: T.textMute,
        }}
      >
        PESACORE
      </motion.div>
    </motion.div>
  );
}

/* ── SVG CONNEXIONS ─────────────────────────────────────────── */
function ConnectionSVG({ svgRef, phase, paths, activeColor }) {
  function ep(d) {
    if (!d) return { x:0, y:0 };
    const pts = d.trim().split(' ');
    return { x:parseFloat(pts[pts.length-2]), y:parseFloat(pts[pts.length-1]) };
  }
  const lc  = activeColor || T.accent;
  const lc2 = activeColor || T.indigo;
  return (
    <svg ref={svgRef} style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:5, overflow:'visible' }}>
      <defs>
        <filter id="gc"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        <filter id="gs"><feGaussianBlur stdDeviation="5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <AnimatePresence>
        {phase >= 1 && paths.right && (
          <motion.g key="right" filter={activeColor ? "url(#gs)" : "url(#gc)"}>
            <motion.path d={paths.right} fill="none" stroke={lc} strokeWidth={activeColor?12:6} strokeOpacity={0.04} strokeLinecap="round"/>
            <motion.path d={paths.right} fill="none" stroke={lc} strokeWidth={activeColor?1.5:1} strokeOpacity={activeColor?0.8:0.45} strokeLinecap="round" initial={{ pathLength:0 }} animate={{ pathLength:1 }} transition={{ duration:1, ease:[0.22,1,0.36,1] }}/>
            {[0.2,0.5,0.8].map((t,i) => (
              <motion.circle key={i} r={activeColor?2.5:1.8} fill={lc} initial={{ opacity:0 }} animate={{ opacity:[0,1,0] }} transition={{ duration:activeColor?1.2:2, repeat:Infinity, delay:i*0.45+0.4 }} style={{ offsetPath:`path('${paths.right}')`, offsetDistance:`${t*100}%` }}/>
            ))}
            <motion.circle cx={ep(paths.right).x} cy={ep(paths.right).y} r={3.5} fill={lc} fillOpacity={0.15} stroke={lc} strokeWidth={1} strokeOpacity={0.6} animate={{ scale:[0.7,1.4,0.7], opacity:[0.3,0.8,0.3] }} transition={{ duration:2, repeat:Infinity, delay:1 }}/>
          </motion.g>
        )}
        {phase >= 2 && paths.left && (
          <motion.g key="left" filter={activeColor ? "url(#gs)" : "url(#gc)"}>
            <motion.path d={paths.left} fill="none" stroke={lc2} strokeWidth={activeColor?12:6} strokeOpacity={0.04} strokeLinecap="round"/>
            <motion.path d={paths.left} fill="none" stroke={lc2} strokeWidth={activeColor?1.5:1} strokeOpacity={activeColor?0.8:0.45} strokeLinecap="round" initial={{ pathLength:0 }} animate={{ pathLength:1 }} transition={{ duration:1, ease:[0.22,1,0.36,1] }}/>
            {[0.2,0.5,0.8].map((t,i) => (
              <motion.circle key={i} r={activeColor?2.5:1.8} fill={lc2} initial={{ opacity:0 }} animate={{ opacity:[0,1,0] }} transition={{ duration:activeColor?1.2:2, repeat:Infinity, delay:i*0.45+0.3 }} style={{ offsetPath:`path('${paths.left}')`, offsetDistance:`${t*100}%` }}/>
            ))}
            <motion.circle cx={ep(paths.left).x} cy={ep(paths.left).y} r={3.5} fill={lc2} fillOpacity={0.15} stroke={lc2} strokeWidth={1} strokeOpacity={0.6} animate={{ scale:[0.7,1.4,0.7], opacity:[0.3,0.8,0.3] }} transition={{ duration:2, repeat:Infinity, delay:1.3 }}/>
          </motion.g>
        )}
        {paths.crossLinks && paths.crossLinks.map((cl,i) => (
          <motion.g key={`x${i}`} initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.35 }}>
            <motion.path d={cl.d} fill="none" stroke={cl.color} strokeWidth={0.8} strokeOpacity={0.35} strokeDasharray="3 5"/>
            <motion.circle r={2} fill={cl.color} fillOpacity={0.7} animate={{ opacity:[0.2,0.9,0.2] }} transition={{ duration:1.8, repeat:Infinity, delay:i*0.3 }} style={{ offsetPath:`path('${cl.d}')`, offsetDistance:'50%' }}/>
          </motion.g>
        ))}
      </AnimatePresence>
    </svg>
  );
}

/* ── HEADER COMMUN DES PANNEAUX ─────────────────────────────── */
function PanelHeader({ title, subtitle, icon, color, side, onClose, children }) {
  return (
    <div style={{ position:'relative', zIndex:10, padding:'1.2rem 1.4rem 1rem', borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.9rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          <motion.div animate={{ opacity:[0.5,1,0.5] }} transition={{ duration:2, repeat:Infinity }}
            style={{ width:6, height:6, borderRadius:'50%', background:color, boxShadow:`0 0 10px ${color}` }} />
          <span style={{ fontSize:'0.58rem', letterSpacing:'0.28em', color:'rgba(255,255,255,0.32)', textTransform:'uppercase' }}>{title}</span>
        </div>
        <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.95 }} onClick={onClose}
          style={{ width:28, height:28, borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.42)', cursor:'pointer' }}>
          <X size={12} />
        </motion.button>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.9rem' }}>
        {icon && <span style={{ fontSize:'1.6rem', flexShrink:0 }}>{icon}</span>}
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:'1rem', fontWeight:700, color:'rgba(255,255,255,0.92)', letterSpacing:'-0.01em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

/* ── SECTION LABEL ──────────────────────────────────────────── */
function SecLabel({ children }) {
  return <div style={{ fontSize:'0.58rem', letterSpacing:'0.24em', color:'rgba(255,255,255,0.28)', textTransform:'uppercase', marginBottom:'0.55rem' }}>{children}</div>;
}

/* ── PIED COMMUN DES PANNEAUX ───────────────────────────────── */
function PanelFooter({ color, label }) {
  return (
    <div style={{ position:'relative', zIndex:10, padding:'0.9rem 1.4rem', borderTop:'1px solid rgba(255,255,255,0.05)', display:'flex', flexDirection:'column', gap:'0.45rem', flexShrink:0 }}>
      <Link to="/home-page"
        style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', padding:'0.75rem', borderRadius:'9px', background:`linear-gradient(135deg,${color}22,${color}10)`, border:`1px solid ${color}32`, color, fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', textDecoration:'none' }}>
        <Zap size={11} /> {label || 'Entrer dans PESACORE'}
      </Link>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.48rem', color:'rgba(255,255,255,0.2)', letterSpacing:'0.1em' }}>
        <span>PESACORE Architect · bêta</span>
        <span style={{ color:'rgba(16,185,129,0.55)' }}>● NOMINAL</span>
      </div>
    </div>
  );
}

/* ── STYLE COMMUN DES PANNEAUX ──────────────────────────────── */
function panelStyle(side, color) {
  const base = {
    position:'fixed', top:0, bottom:0, zIndex:500,
    width:'min(420px,90vw)',
    display:'flex', flexDirection:'column',
    fontFamily:"'DM Mono',monospace",
    background:'rgba(5,8,14,0.97)',
    backdropFilter:'blur(18px)',
    WebkitBackdropFilter:'blur(18px)',
    overflow:'hidden',
  };
  if (side === 'left')  return { ...base, left:0,  borderRight:'1px solid rgba(255,255,255,0.07)', boxShadow:`8px 0 52px rgba(0,0,0,0.75),0 0 0 1px ${color}12` };
  return                       { ...base, right:0, borderLeft: '1px solid rgba(255,255,255,0.07)', boxShadow:`-8px 0 52px rgba(0,0,0,0.75),0 0 0 1px ${color}12` };
}

function PanelAccentLine({ side, color }) {
  const pos = side === 'left' ? { right:0 } : { left:0 };
  return <div style={{ position:'absolute', ...pos, top:0, bottom:0, width:2, background:`linear-gradient(180deg,transparent,${color}65,${color}28,transparent)` }} />;
}

function PanelScanlines() {
  return <div style={{ position:'absolute', inset:0, pointerEvents:'none', backgroundImage:'repeating-linear-gradient(0deg,rgba(255,255,255,0.012) 0,rgba(255,255,255,0.012) 1px,transparent 1px,transparent 4px)' }} />;
}

/* ── PANNEAU PARTICULIER ────────────────────────────────────── */
function ParticulierPanel({ onClose }) {
  const color = T.accent;
  const [sel, setSel] = useState(null);

  return (
    <motion.div initial={{ x:'-100%', opacity:0 }} animate={{ x:0, opacity:1 }} exit={{ x:'-100%', opacity:0 }} transition={{ duration:0.4, ease:[0.22,1,0.36,1] }} style={panelStyle('left', color)}>
      <PanelAccentLine side="left" color={color} />
      <PanelScanlines />

      <PanelHeader title="ACCÈS INDIVIDUEL" subtitle="Particulier & Autodidacte" icon="👤" color={color} onClose={onClose} />

      <div className="scrollbar-hide" style={{ flex:1, overflowY:'auto', padding:'1.2rem 1.4rem', display:'flex', flexDirection:'column', gap:'1.1rem', position:'relative', zIndex:10 }}>

        {/* Accroche */}
        <div style={{ padding:'0.75rem 0.9rem', borderRadius:'9px', background:`${color}08`, border:`1px solid ${color}16` }}>
          <p style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.62)', lineHeight:1.6 }}>PESACORE n'est pas un LMS de contenu. C'est un moteur de simulation causale. Vous naviguez en traversant des situations réelles, à votre rythme.</p>
        </div>

        {/* Comment ça marche */}
        <div>
          <SecLabel>Comment ça fonctionne</SecLabel>
          {[
            { s:'01', l:'Accédez à la marketplace',     d:'Parcourez les templates et architectures disponibles.' },
            { s:'02', l:'Choisissez une situation',      d:'Construite par un professionnel ou un OF partenaire.' },
            { s:'03', l:'Traversez, décidez, bifurquez', d:'Vos choix sont tracés — chaque décision laisse une empreinte exploitable.' },
            { s:'04', l:'Consultez vos traces',          d:'Débrief automatique, progression documentée, certification individuelle.' },
          ].map((it,i) => (
            <div key={i} style={{ display:'flex', gap:'0.6rem', padding:'0.5rem 0.7rem', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.05)', background:'rgba(255,255,255,0.02)', marginBottom:'0.35rem' }}>
              <span style={{ fontSize:'0.52rem', color, fontWeight:700, flexShrink:0, marginTop:'0.15rem', letterSpacing:'0.1em' }}>{it.s}</span>
              <div>
                <div style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.8)', fontWeight:600, marginBottom:'0.1rem' }}>{it.l}</div>
                <div style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.38)', lineHeight:1.45 }}>{it.d}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Plans */}
        <div>
          <SecLabel>Plans individuels</SecLabel>
          {PLANS_PART.map(plan => {
            const isSel = sel === plan.id;
            return (
              <motion.div key={plan.id} onClick={() => setSel(isSel ? null : plan.id)}
                animate={{ borderColor: isSel ? plan.color : 'rgba(255,255,255,0.06)', background: isSel ? `${plan.color}09` : 'rgba(255,255,255,0.02)' }}
                style={{ padding:'0.6rem 0.75rem', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.06)', cursor:'pointer', marginBottom:'0.3rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: isSel ? '0.5rem' : 0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.45rem' }}>
                    <div style={{ width:4, height:4, borderRadius:'50%', background:plan.color }} />
                    <span style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.85)', fontWeight:700 }}>{plan.name}</span>
                    {plan.trial && <span style={{ fontSize:'0.48rem', color:plan.color, letterSpacing:'0.1em' }}>Essai {plan.trial}</span>}
                  </div>
                  <div style={{ display:'flex', alignItems:'baseline', gap:'0.12rem' }}>
                    <span style={{ fontSize:'0.85rem', color:plan.color, fontWeight:700 }}>{plan.price}</span>
                    <span style={{ fontSize:'0.5rem', color:'rgba(255,255,255,0.28)' }}>{plan.period}</span>
                  </div>
                </div>
                <AnimatePresence>
                  {isSel && (
                    <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }} style={{ overflow:'hidden' }}>
                      {plan.features.map((f,i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginTop:'0.2rem' }}>
                          <Check size={9} color={T.green} />
                          <span style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.52)' }}>{f}</span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Marketplace */}
        <div style={{ padding:'0.65rem 0.8rem', borderRadius:'8px', border:`1px solid ${T.indigo}18`, background:`${T.indigo}05` }}>
          <div style={{ fontSize:'0.58rem', color:T.indigoG, letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'0.3rem' }}>Marketplace & Partenaires</div>
          <p style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.42)', lineHeight:1.5 }}>En tant que particulier, vous accédez aux parcours publiés par nos partenaires (OF, universités, entreprises) sans gérer la configuration.</p>
        </div>

        {/* Orientation pro */}
        <div style={{ padding:'0.6rem 0.8rem', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.05)', background:'rgba(255,255,255,0.02)' }}>
          <div style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.25)', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:'0.25rem' }}>Vous êtes un professionnel ?</div>
          <p style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.38)', lineHeight:1.5 }}>Les plans Academia, Pro et Entreprise donnent accès au dashboard admin, cohortes, CRM et Qualiopi. Sélectionnez la cible correspondante.</p>
        </div>
      </div>

      <PanelFooter color={color} label="Commencer gratuitement" />
    </motion.div>
  );
}

/* ── PANNEAU PROFIL B2B ─────────────────────────────────────── */
function ProfileSidePanel({ entity, onClose }) {
  const profileIds = entity.profileIds || [];
  const [activeProfileId, setActiveProfileId] = useState(profileIds[0] || null);
  const [expandedTree, setExpandedTree]       = useState(null);

  const profile  = activeProfileId ? PROFILES[activeProfileId] : null;
  const color    = profileColor(profile) || entity.color || T.accent;
  const certFw   = profile?.certificationFramework;
  const financeurs = certFw?.financeurs?.slice(0,4) || [];
  const saleArgs   = certFw?.saleArguments?.slice(0,3) || [];
  const treeData   = PERSONA_TREE[activeProfileId] || [];

  // Reset tree quand on change de profil
  useEffect(() => { setExpandedTree(null); }, [activeProfileId]);

  const hasQualiopi = certFw?.primary === 'qualiopi' || certFw?.secondary?.includes('qualiopi');
  const hasDPC      = certFw?.primary === 'dpc';

  return (
    <motion.div initial={{ x:'-100%', opacity:0 }} animate={{ x:0, opacity:1 }} exit={{ x:'-100%', opacity:0 }} transition={{ duration:0.4, ease:[0.22,1,0.36,1] }} style={panelStyle('left', color)}>
      <PanelAccentLine side="left" color={color} />
      <PanelScanlines />

      {/* Header */}
      <PanelHeader title="PROFIL ACTIF" subtitle={entity.label} icon={profile?.icon} color={color} onClose={onClose}>
        {/* Switcher de profils si > 1 */}
        
          <div style={{ display:'flex', gap:'0.35rem', marginTop:'0.85rem', flexWrap:'wrap' }}>
            {profileIds.map(pid => {
              const pr = PROFILES[pid];
              const pc = profileColor(pr);
              const isActive = pid === activeProfileId;
              return (
                <motion.button key={pid} onClick={() => setActiveProfileId(pid)}
                  animate={{ borderColor: isActive ? pc : 'rgba(255,255,255,0.08)', background: isActive ? `${pc}14` : 'rgba(0,0,0,0)', color: isActive ? pc : 'rgba(255,255,255,0.38)' }}
                  transition={{ duration:0.2 }}
                  style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.28rem 0.6rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.08)', cursor:'pointer', fontFamily:"'DM Mono',monospace", fontSize:'0.58rem', letterSpacing:'0.08em', fontWeight: isActive ? 700 : 400 }}>
                  {pr?.icon && <span style={{ fontSize:'0.85rem' }}>{pr.icon}</span>}
                  {pr?.label || pid}
                </motion.button>
              );
            })}
          </div>
        
        {/* Badges certification */}
        <div style={{ display:'flex', gap:'0.35rem', marginTop:'0.5rem' }}>
          {hasQualiopi && <span style={{ fontSize:'0.46rem', padding:'0.18rem 0.45rem', borderRadius:'4px', border:`1px solid ${T.green}35`, color:T.green, letterSpacing:'0.12em' }}>QUALIOPI</span>}
          {hasDPC      && <span style={{ fontSize:'0.46rem', padding:'0.18rem 0.45rem', borderRadius:'4px', border:`1px solid ${T.indigo}35`, color:T.indigo, letterSpacing:'0.12em' }}>DPC</span>}
          {certFw?.secondary?.includes('rncp') && <span style={{ fontSize:'0.46rem', padding:'0.18rem 0.45rem', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.38)', letterSpacing:'0.12em' }}>RNCP</span>}
        </div>
      </PanelHeader>

      <div className="scrollbar-hide" style={{ flex:1, overflowY:'auto', padding:'1.2rem 1.4rem', display:'flex', flexDirection:'column', gap:'1.1rem', position:'relative', zIndex:10 }}>

        {/* Description profil */}
        {profile?.description && (
          <div style={{ padding:'0.65rem 0.8rem', borderRadius:'8px', background:`${color}07`, border:`1px solid ${color}14` }}>
            <p style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.58)', lineHeight:1.55 }}>{profile.description}</p>
          </div>
        )}

        {/* Arbre personas */}
        {treeData.length > 0 && (
          <div>
            <SecLabel>Types d'organisations</SecLabel>
            {treeData.map((branch,i) => {
              const isExp = expandedTree === i;
              return (
                <div key={i} style={{ marginBottom:'0.3rem' }}>
                  <motion.div onClick={() => setExpandedTree(isExp ? null : i)}
                    style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.45rem 0.65rem', borderRadius:'7px', border:`1px solid ${isExp ? color+'30' : 'rgba(255,255,255,0.06)'}`, background: isExp ? `${color}07` : 'rgba(255,255,255,0.02)', cursor: branch.sub.length > 0 ? 'pointer' : 'default' }}>
                    <div style={{ width:4, height:4, borderRadius:'50%', background: isExp ? color : 'rgba(255,255,255,0.22)', flexShrink:0 }} />
                    <span style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.75)', flex:1 }}>{branch.label}</span>
                    {branch.sub.length > 0 && (
                      <motion.div animate={{ rotate: isExp ? 90 : 0 }} transition={{ duration:0.2 }}>
                        <ChevronRight size={10} color={isExp ? color : 'rgba(255,255,255,0.22)'} />
                      </motion.div>
                    )}
                  </motion.div>
                  <AnimatePresence>
                    {isExp && branch.sub.length > 0 && (
                      <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }} style={{ overflow:'hidden', marginTop:'0.2rem', marginLeft:'1rem', display:'flex', flexDirection:'column', gap:'0.2rem' }}>
                        {branch.sub.map((s,j) => (
                          <div key={j} style={{ display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.3rem 0.55rem', borderRadius:'5px', border:'1px solid rgba(255,255,255,0.04)', background:'rgba(255,255,255,0.015)' }}>
                            <div style={{ width:2, height:2, borderRadius:'50%', background:`${color}55` }} />
                            <span style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.48)' }}>{s}</span>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}

        {/* Financeurs */}
        {financeurs.length > 0 && (
          <div>
            <SecLabel>Financeurs éligibles</SecLabel>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.32rem' }}>
              {financeurs.map(f => (
                <div key={f.id} style={{ display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.5rem 0.7rem', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.05)', background:'rgba(255,255,255,0.02)' }}>
                  <div style={{ width:4, height:4, borderRadius:'50%', background:color, flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.75)', fontWeight:600 }}>{f.label}</span>
                    <span style={{ fontSize:'0.56rem', color:'rgba(255,255,255,0.3)', marginLeft:'0.4rem' }}>{f.description}</span>
                  </div>
                  {f.url && (
                    <a href={f.url} target="_blank" rel="noreferrer"
                      style={{ color:'rgba(255,255,255,0.28)', flexShrink:0 }}
                      onClick={e => e.stopPropagation()}>
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bénéfices */}
        {saleArgs.length > 0 && (
          <div>
            <SecLabel>Bénéfices clés</SecLabel>
            {saleArgs.map((arg,i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'0.5rem', padding:'0.5rem 0.7rem', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.05)', background:'rgba(255,255,255,0.02)', marginBottom:'0.3rem' }}>
                <span style={{ color:T.green, fontSize:'0.65rem', flexShrink:0, marginTop:'0.05rem' }}>✓</span>
                <span style={{ fontSize:'0.66rem', color:'rgba(255,255,255,0.62)', lineHeight:1.5 }}>{arg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Plans org */}
        <div style={{ padding:'0.65rem 0.8rem', borderRadius:'8px', border:`1px solid ${T.indigo}14`, background:`${T.indigo}04` }}>
          <div style={{ fontSize:'0.58rem', color:T.indigoG, letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:'0.3rem' }}>Plans organisation</div>
          <p style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.4)', lineHeight:1.5 }}>Academia · Pro · Entreprise — dashboard admin, cohortes, CRM, exports réglementaires.</p>
          <div style={{ display:'flex', gap:'0.35rem', marginTop:'0.5rem', flexWrap:'wrap' }}>
            {['Academia','Pro','Entreprise'].map(pl => (
              <span key={pl} style={{ padding:'0.2rem 0.45rem', borderRadius:'4px', border:`1px solid ${color}28`, fontSize:'0.52rem', color }}>{pl}</span>
            ))}
          </div>
        </div>
      </div>

      <PanelFooter color={color} label="Entrer dans PESACORE" />
    </motion.div>
  );
}

/* ── PANNEAU SERVICE ─────────────────────────────────────────── */
function ServiceSidePanel({ entity, onClose }) {
  const color    = entity.color || T.accentG;
  const metrics  = (METRICS[entity.id] || []).slice(0,3);
  const compatPs = (entity.compatProfiles || []).map(pid => PROFILES[pid]).filter(Boolean);
  const connectEntities = (entity.connectTo || []).map(id => ALL_ENTITIES.find(e => e.id === id)).filter(Boolean);

  return (
    <motion.div initial={{ x:'100%', opacity:0 }} animate={{ x:0, opacity:1 }} exit={{ x:'100%', opacity:0 }} transition={{ duration:0.4, ease:[0.22,1,0.36,1] }} style={panelStyle('right', color)}>
      <PanelAccentLine side="right" color={color} />
      <PanelScanlines />

      {/* Header */}
      <div style={{ position:'relative', zIndex:10, padding:'1.2rem 1.4rem 1rem', borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.9rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <motion.div animate={{ opacity:[0.5,1,0.5] }} transition={{ duration:2, repeat:Infinity }}
              style={{ width:6, height:6, borderRadius:'50%', background:color, boxShadow:`0 0 10px ${color}` }} />
            <span style={{ fontSize:'0.58rem', letterSpacing:'0.28em', color:'rgba(255,255,255,0.32)', textTransform:'uppercase' }}>MODULE ACTIF</span>
          </div>
          <motion.button whileHover={{ scale:1.1 }} whileTap={{ scale:0.95 }} onClick={onClose}
            style={{ width:28, height:28, borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.42)', cursor:'pointer' }}>
            <X size={12} />
          </motion.button>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.9rem' }}>
          <div style={{ width:42, height:42, borderRadius:'11px', background:`${color}12`, border:`1px solid ${color}25`, display:'flex', alignItems:'center', justifyContent:'center', color, flexShrink:0 }}>
            <entity.icon size={18} strokeWidth={1.5} />
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'1rem', fontWeight:700, color:'rgba(255,255,255,0.92)' }}>{entity.label}</div>
            <div style={{ fontSize:'0.64rem', color:'rgba(255,255,255,0.38)', marginTop:'0.18rem' }}>{entity.detail}</div>
          </div>
        </div>
      </div>

      <div className="scrollbar-hide" style={{ flex:1, overflowY:'auto', padding:'1.2rem 1.4rem', display:'flex', flexDirection:'column', gap:'1.1rem', position:'relative', zIndex:10 }}>

        {/* Indicateurs */}
        {metrics.length > 0 && (
          <div>
            <SecLabel>Indicateurs système</SecLabel>
            {metrics.map((m,i) => (
              <div key={i} style={{ padding:'0.6rem 0.8rem', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.05)', background:'rgba(255,255,255,0.02)', marginBottom:'0.35rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: m.max ? '0.4rem' : 0 }}>
                  <span style={{ fontSize:'0.64rem', color:'rgba(255,255,255,0.38)' }}>{m.k}</span>
                  <span style={{ fontSize:'0.9rem', fontWeight:700, color:'rgba(255,255,255,0.88)' }}>
                    {m.v}<span style={{ fontSize:'0.5rem', color:'rgba(255,255,255,0.28)', marginLeft:'0.15rem' }}>{m.u}</span>
                  </span>
                </div>
                {m.max !== undefined && (
                  <div style={{ height:2, borderRadius:1, background:'rgba(255,255,255,0.06)', overflow:'hidden' }}>
                    <motion.div initial={{ width:0 }} animate={{ width:`${(m.v/m.max)*100}%`}} transition={{ duration:1.2, ease:'easeOut', delay:i*0.1 }}
                      style={{ height:'100%', borderRadius:1, background:`linear-gradient(90deg,${color},${color}65)` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Profils compatibles — avec badges cert */}
        {compatPs.length > 0 && (
          <div>
            <SecLabel>Profils compatibles</SecLabel>
            {compatPs.map(p => {
              const pc = profileColor(p);
              const hasDpc = p.certificationFramework?.primary === 'dpc';
              const hasQ   = p.certificationFramework?.primary === 'qualiopi' || p.certificationFramework?.secondary?.includes('qualiopi');
              return (
                <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.55rem 0.7rem', borderRadius:'8px', border:`1px solid ${pc}1e`, background:`${pc}05`, marginBottom:'0.3rem' }}>
                  <span style={{ fontSize:'1rem', flexShrink:0 }}>{p.icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.8)', fontWeight:600 }}>{p.label}</div>
                    <div style={{ fontSize:'0.56rem', color:'rgba(255,255,255,0.32)', marginTop:'0.08rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.description}</div>
                  </div>
                  <div style={{ display:'flex', gap:'0.25rem', flexShrink:0 }}>
                    {hasQ   && <span style={{ fontSize:'0.38rem', padding:'0.14rem 0.35rem', borderRadius:'3px', border:`1px solid ${T.green}30`, color:T.green }}>Q</span>}
                    {hasDpc && <span style={{ fontSize:'0.38rem', padding:'0.14rem 0.35rem', borderRadius:'3px', border:`1px solid ${T.indigo}30`, color:T.indigo }}>DPC</span>}
                  </div>
                  <ArrowRight size={9} color={pc} />
                </div>
              );
            })}
          </div>
        )}

        {/* Liaisons */}
        {connectEntities.length > 0 && (
          <div>
            <SecLabel>Liaisons causales</SecLabel>
            <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
              {connectEntities.map(target => (
                <div key={target.id} style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.35rem 0.6rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.02)' }}>
                  <Activity size={9} color={color} />
                  <span style={{ fontSize:'0.64rem', color:'rgba(255,255,255,0.62)' }}>{target.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Note technique */}
        <div style={{ padding:'0.65rem 0.8rem', borderRadius:'8px', border:`1px solid rgba(255,255,255,0.05)`, background:'rgba(255,255,255,0.015)' }}>
          <div style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.25)', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:'0.3rem' }}>Architecture</div>
          <p style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.38)', lineHeight:1.55 }}>Chaque module s'instancie sur le moteur causal découplé. Aucune dépendance à un modèle tiers.</p>
        </div>
      </div>

      <PanelFooter color={color} label="Entrer dans PESACORE" />
    </motion.div>
  );
}

/* ── COMPOSANT PRINCIPAL ────────────────────────────────────── */
export default function LandingPesacore() {
  const [phase, setPhase]               = useState(0);
  const [cycleIndex, setCycleIndex]     = useState(0);
  const [manualEntity, setManualEntity] = useState(null);
  const [activePanel, setActivePanel]   = useState(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const logoRef     = useRef(null);
  const rightColRef = useRef(null);
  const leftColRef  = useRef(null);
  const svgRef      = useRef(null);
  const cardRefs    = useRef({});
  const [paths, setPaths] = useState({ right:'', left:'', crossLinks:[] });

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 600);
    const t2 = setTimeout(() => setPhase(2), 1600);
    const t3 = setTimeout(() => setPhase(3), 2600);
    return () => [t1,t2,t3].forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase < 3) return;
    const iv = setInterval(() => { if (!manualEntity) setCycleIndex(p => (p+1) % ALL_ENTITIES.length); }, 4200);
    return () => clearInterval(iv);
  }, [phase, manualEntity]);

  const activeId     = manualEntity || (phase >= 3 ? ALL_ENTITIES[cycleIndex]?.id : null);
  const activeEntity = activeId ? ALL_ENTITIES.find(e => e.id === activeId) : null;
  const activeColor  = activeEntity?.color || null;

  function handleActivate(id) {
    if (manualEntity === id) { setManualEntity(null); setActivePanel(null); return; }
    setManualEntity(id);
    const entity = ALL_ENTITIES.find(e => e.id === id);
    if (id === 'particuliers') {
      setActivePanel({ type:'particulier', entity });
    } else {
      const isCible = CIBLES.some(c => c.id === id);
      if (isCible) {
        setActivePanel({ type:'profile', entity });
      } else {
        const svc = SERVICES.find(s => s.id === id);
        setActivePanel({ type:'service', entity: svc || entity });
      }
    }
  }

  function closePanel() { setManualEntity(null); setActivePanel(null); }

  const calcPaths = useCallback(() => {
    if (isMobile) return;
    const svg = svgRef.current;
    if (!svg || !logoRef.current) return;
    const sr = svg.getBoundingClientRect();

    function bezier(fromEl, toEl, side) {
      if (!fromEl || !toEl) return '';
      const fr = fromEl.getBoundingClientRect(), tr = toEl.getBoundingClientRect();
      const ox = fr.left + fr.width/2  - sr.left, oy = fr.top  + fr.height/2 - sr.top;
      const tx = side === 'right' ? tr.left - sr.left : tr.right - sr.left;
      const ty = tr.top  + tr.height/2 - sr.top;
      const dx = tx - ox;
      return `M ${ox} ${oy} C ${ox+dx*0.38} ${oy+(ty-oy)*0.06} ${ox+dx*0.68} ${ty-(ty-oy)*0.06} ${tx} ${ty}`;
    }

    let crossLinks = [];
    if (activeEntity) {
      activeEntity.connectTo?.forEach(targetId => {
        const fromEl = cardRefs.current[activeId], toEl = cardRefs.current[targetId];
        if (fromEl && toEl) {
          const fr = fromEl.getBoundingClientRect(), tr = toEl.getBoundingClientRect();
          const fx = fr.left+fr.width/2-sr.left, fy = fr.top+fr.height/2-sr.top;
          const tx2 = tr.left+tr.width/2-sr.left, ty2 = tr.top+tr.height/2-sr.top;
          crossLinks.push({ d:`M ${fx} ${fy} Q ${(fx+tx2)/2} ${(fy+ty2)/2-48} ${tx2} ${ty2}`, color:activeColor });
        }
      });
    }
    setPaths({ right:bezier(logoRef.current, rightColRef.current,'right'), left:bezier(logoRef.current, leftColRef.current,'left'), crossLinks });
  }, [isMobile, activeId, activeEntity, activeColor]);

  useLayoutEffect(() => {
    calcPaths();
    const ro = new ResizeObserver(calcPaths);
    [logoRef,rightColRef,leftColRef].forEach(r => { if (r.current) ro.observe(r.current); });
    window.addEventListener('resize', calcPaths);
    return () => { ro.disconnect(); window.removeEventListener('resize', calcPaths); };
  }, [calcPaths, phase]);

  const panelOverlay = (
    <AnimatePresence>
      {activePanel?.type === 'particulier' && <ParticulierPanel onClose={closePanel} />}
      {activePanel?.type === 'profile'     && <ProfileSidePanel entity={activePanel.entity} onClose={closePanel} />}
      {activePanel?.type === 'service'     && <ServiceSidePanel entity={activePanel.entity} onClose={closePanel} />}
    </AnimatePresence>
  );

  /* ── MOBILE ─────────────────────────────────────────────── */
  if (isMobile) {
    return (
      <div className="scrollbar-hide" style={{
        width: '100vw', minHeight: '100dvh', background: T.bg,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: 'clamp(20px,5vh,40px) 0.75rem 2rem',   // padding horizontal réduit
        gap: 'clamp(16px,3.5vh,28px)',
        fontFamily: "'DM Mono','Inter',system-ui,sans-serif",
        overflowY: 'auto', overflowX: 'hidden', position: 'relative',
        boxSizing: 'border-box',                        // indispensable
      }}>
        {/* Grille de fond (inchangée) */}
        <div className="scrollbar-hide" style={{ position: 'fixed', inset: 0, pointerEvents: 'none',
          backgroundImage: `linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`,
          backgroundSize: '48px 48px', opacity: 0.28 }} />

        {/* Titre */}
        <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.15 }}
      className="text-center space-y-4 mb-10"
    >
      {/* Badge */}
      <p className="font-mono text-[0.5rem] sm:text-[0.6rem] tracking-[0.35em] uppercase" style={{ color: T.accent, opacity: 0.8 }}>
        PESACORE Architect 2026
      </p>

      {/* Titre principal */}
      <h1 className="font-serif font-light text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight" style={{ color: T.text }}>
        Cartographier les trajectoires.{' '}
        <strong className="font-bold">Traverser</strong> une situation.
      </h1>

      {/* Accroche en italique avec animation plus douce */}
      <motion.em
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="block font-serif not-italic text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto"
        style={{ color: T.accent, opacity: 0.7 }}
      >
        Immersion décisionnelle · Analyse comportementale · Mesure d'impact.
      </motion.em>
    </motion.div>

        <LogoHub
          logoRef={logoRef}
          phase={phase}
          activeColor={activeColor}
          onClick={() => navigate('/home-page')}
        />

        {/* Cibles (deux colonnes fluides) */}
        {phase >= 1 && (
          <motion.div ref={rightColRef} initial={{ opacity:0 }} animate={{ opacity:1 }}
            style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}>
            <p style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.52rem', letterSpacing: '0.26em', textTransform: 'uppercase', color: T.accent, opacity: 0.45, textAlign: 'center', marginBottom: '0.5rem' }}>
              Pour qui
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',   // évite le débordement
              gap: '0.45rem',
            }}>
              {CIBLES.map(c => (
                <div key={c.id} ref={el => cardRefs.current[c.id] = el}>
                  <ControlPanel {...c} isActive={activeId===c.id} isDimmed={activeId!==null && activeId!==c.id} onActivate={handleActivate} />
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Services (deux colonnes fluides) */}
        {phase >= 2 && (
          <motion.div ref={leftColRef} initial={{ opacity:0 }} animate={{ opacity:1 }}
            style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}>
            <p style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.52rem', letterSpacing: '0.26em', textTransform: 'uppercase', color: T.indigo, opacity: 0.45, textAlign: 'center', marginBottom: '0.5rem' }}>
              Notre métier
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: '0.45rem',
            }}>
              {SERVICES.map(s => (
                <div key={s.id} ref={el => cardRefs.current[s.id] = el}>
                  <ControlPanel {...s} desc={s.detail} isActive={activeId===s.id} isDimmed={activeId!==null && activeId!==s.id} onActivate={handleActivate} />
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Pied */}
        {phase >= 3 && (
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
            style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <p style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.54rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textMute, marginTop: '0.5rem' }}>
              Aucune CB · Première expérience offerte
            </p>
          </motion.div>
        )}

        {panelOverlay}
      </div>
    );
  }

  /* ── DESKTOP ─────────────────────────────────────────────── */
  return (
    <div className="scrollbar-hide" style={{ scrollbar: 'hidden', width:'100vw', height:'100dvh', background:T.bg, position:'relative', overflow:'hidden', fontFamily:"'DM Mono','Inter',system-ui,sans-serif", display:'flex', flexDirection:'column' }}>

      <div className="scrollbar-hide" style={{ position:'absolute', inset:0, pointerEvents:'none', backgroundImage:`linear-gradient(${T.border} 1px,transparent 1px),linear-gradient(90deg,${T.border} 1px,transparent 1px)`, backgroundSize:'52px 52px', opacity:0.32 }} />

      <ConnectionSVG svgRef={svgRef} phase={phase} paths={paths} activeColor={activeColor} />

      {/* Barre top */}
      <Header />

      {/* Corps 3 colonnes */}
      <div className="scrollbar-hide pt-24" style={{ flex:1, display:'grid', gridTemplateColumns:'clamp(235px,27vw,315px) 1fr clamp(235px,27vw,315px)', gap:0, position:'relative', overflow:'hidden' }}>

        {/* Gauche : SERVICES */}
        <AnimatePresence>
          {phase >= 2 && (
            <motion.div ref={leftColRef} initial={{ opacity:0, x:-20 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.55, ease:[0.22,1,0.36,1] }}
              style={{ borderRight:`1px solid ${T.border}`, padding:'1.4rem 1.1rem', display:'flex', flexDirection:'column', gap:'0.7rem', overflowY:'auto', position:'relative', zIndex:10, background:`${T.bgPanel}85` }}>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.66rem', letterSpacing:'0.32em', textTransform:'uppercase', color:T.indigo, opacity:0.48, paddingLeft:'0.1rem' }}>Notre métier</span>
              {SERVICES.map(s => (
                <div key={s.id} ref={el => cardRefs.current[s.id] = el}>
                  <ControlPanel {...s} desc={s.detail} isActive={activeId===s.id} isDimmed={activeId!==null && activeId!==s.id} onActivate={handleActivate} />
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Centre */}
        <div className="scrollbar-hide relative flex flex-col items-center justify-center min-h-[70vh] py-12 px-6 overflow-hidden" style={{ zIndex: 8 }}>
  
  {/* ── Grille de fond ── */}
  <CausalGrid activeColor={activeColor} />

  {/* ── Contenu principal ── */}
  <div className="relative z-10 flex flex-col items-center w-full max-w-3xl mx-auto">
    
    {/* Bloc titre + sous-titre */}
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.15 }}
      className="text-center space-y-4 mb-10"
    >
      {/* Badge */}
      <p className="font-mono text-[0.5rem] sm:text-[0.6rem] tracking-[0.35em] uppercase" style={{ color: T.accent, opacity: 0.4 }}>
        PESACORE Architect 2026
      </p>

      {/* Titre principal */}
      <h1 className="font-serif font-light text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight" style={{ color: T.text }}>
        Cartographier les trajectoires.{' '}
        <strong className="font-bold">Traverser</strong> une situation.
      </h1>

      {/* Accroche en italique avec animation plus douce */}
      <motion.em
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="block font-serif not-italic text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto"
        style={{ color: T.accent, opacity: 0.7 }}
      >
        Immersion décisionnelle · Analyse comportementale · Mesure d'impact.
      </motion.em>
    </motion.div>

    {/* ── Statut du panneau ── */}
    <AnimatePresence mode="wait">
      {activeEntity ? (
        <motion.p
          key={activeId}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="font-mono text-[0.5rem] sm:text-[0.55rem] tracking-[0.25em] uppercase mb-8"
          style={{ color: activeColor }}
        >
          ● {activeEntity.label} · Panneau actif
        </motion.p>
      ) : (
        <motion.p
          key="idle"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 3 ? 0.2 : 0 }}
          className="font-mono text-[0.5rem] sm:text-[0.55rem] tracking-[0.25em] uppercase mb-8"
          style={{ color: T.textMute }}
        >
          Sélectionnez un panneau · système en attente
        </motion.p>
      )}
    </AnimatePresence>

    {/* ── Logo Hub ── */}
    <div className="mb-10">
      <LogoHub
        logoRef={logoRef}
        phase={phase}
        activeColor={activeColor}
        onClick={() => navigate('/home-page')}
      />
    </div>

    {/* ── Footer CTA ── */}
    <AnimatePresence>
      {phase >= 3 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center space-y-4"
        >
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-12 h-px mx-auto"
            style={{ background: `linear-gradient(90deg, transparent, ${T.accent}40, transparent)` }}
          />
          
          <p className="font-mono text-[0.5rem] sm:text-[0.55rem] tracking-[0.25em] uppercase" style={{ color: T.textMute, opacity: 0.5 }}>
            Aucune carte bancaire · Première expérience offerte
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
</div>

        {/* Droite : CIBLES */}
        <AnimatePresence>
          {phase >= 1 && (
            <motion.div ref={rightColRef} initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.55, ease:[0.22,1,0.36,1] }}
              style={{ borderLeft:`1px solid ${T.border}`, padding:'1.4rem 1.1rem', display:'flex', flexDirection:'column', gap:'0.7rem', overflowY:'auto', position:'relative', zIndex:10, background:`${T.bgPanel}85` }}>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.66rem', letterSpacing:'0.32em', textTransform:'uppercase', color:T.accent, opacity:0.48, paddingLeft:'0.1rem' }}>Pour qui</span>
              {CIBLES.map(c => (
                <div key={c.id} ref={el => cardRefs.current[c.id] = el}>
                  <ControlPanel {...c} isActive={activeId===c.id} isDimmed={activeId!==null && activeId!==c.id} onActivate={handleActivate} />
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Barre bottom */}
      <div style={{ position:'relative', zIndex:20, borderTop:`1px solid ${T.border}`, padding:'0.44rem 1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', background:`${T.bgPanel}d0`, backdropFilter:'blur(10px)', flexShrink:0 }}>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.56rem', letterSpacing:'0.22em', color:T.textMute, opacity:0.8 }}>MOTEUR DÉCOUPLÉ · IH SOUVERAINE · © 2026 PESACORE Architect · Tous droits réservés.</span>
        <div style={{ display:'flex', gap:'1.2rem' }}>
          {[['SIM',T.green],['CAU',T.indigo],['INV',T.accent]].map(([tag,col]) => (
            <div key={tag} style={{ display:'flex', alignItems:'center', gap:'0.35rem' }}>
              <div style={{ width:5, height:5, borderRadius:'50%', background:col, boxShadow:`0 0 6px ${col}` }} />
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.56rem', color:T.textMute, letterSpacing:'0.15em' }}>{tag}</span>
            </div>
          ))}
        </div>
      </div>

      {panelOverlay}
    </div>
  );
}